import { useCallback, useEffect, useRef, useState } from "react";
import bwipjs from "bwip-js";
import { CloseCircleFilled, DeleteOutlined, DownloadOutlined } from "@ant-design/icons";
import NoDataPlaceholder from "./common/NoDataPlaceholder";
import ErrorPlaceholder from "./common/ErrorPlaceholder";
import { useNote, TheNote, TheNoteToggleButton } from "./common/TheNote";
import { ItemSizeType } from "./elements/SizeSelect";

interface RenderOneDataMatrixProps {
    data: string;
    index: number;
    itemSize?: ItemSizeType;
    onDataChange: (index: number, value: string) => void;
    onClear: () => void;
    showRemove: boolean;
    onRemove: () => void;
}

function RenderOneDataMatrix({
    data,
    index,
    itemSize,
    onDataChange,
    onClear,
    showRemove,
    onRemove,
}: RenderOneDataMatrixProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [debouncedData, setDebouncedData] = useState(data);
    const [hasError, setHasError] = useState(false);
    const { showNote, setShowNote, note, setNote } = useNote("datamatrix", index);

    // Debounce data changes
    useEffect(() => {
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = setTimeout(() => setDebouncedData(data), 300);
        return () => { if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current); };
    }, [data]);

    // Render D.Matrix on canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const trimmed = debouncedData ? String(debouncedData).trim() : "";
        if (!trimmed) {
            const ctx = canvas.getContext("2d");
            if (ctx) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
            }
            setHasError(false);
            return;
        }
        try {
            bwipjs.toCanvas(canvas, {
                bcid: "datamatrix",
                text: trimmed,
                scale: 4,
                padding: 6,
                backgroundcolor: "ffffff",
                barcolor: "000000",
            });
            setHasError(false);
        } catch (err) {
            console.error("bwip-js DataMatrix error:", err);
            setHasError(true);
        }
    }, [debouncedData]);

    const handleInputClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => { (e.target as HTMLInputElement).select(); }, []);

    const handleClearClick = useCallback(() => {
        onClear();
        setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 10);
    }, [onClear]);

    const handleDownloadPNG = useCallback(() => {
        const trimmed = debouncedData ? String(debouncedData).trim() : "";
        if (!trimmed) return;

        // Create an offscreen canvas to render a higher resolution version for download
        const offscreenCanvas = document.createElement("canvas");
        try {
            bwipjs.toCanvas(offscreenCanvas, {
                bcid: "datamatrix",
                text: trimmed,
                scale: 16, // 4x the display scale (4 * 4)
                padding: 6,
                backgroundcolor: "ffffff",
                barcolor: "000000",
            });

            const link = document.createElement("a");
            link.download = `datamatrix-${String(index + 1).padStart(2, "0")}.png`;
            link.href = offscreenCanvas.toDataURL("image/png");
            link.click();
        } catch (err) {
            console.error("bwip-js download error:", err);
        }
    }, [debouncedData, index]);

    const isEmpty = !debouncedData || !String(debouncedData).trim();
    const canDownload = !isEmpty && !hasError;

    return (
        <div className={`qr-preview size-${itemSize || "medium"} p-4 relative card h-100`}>
            {/* Note */}
            <TheNote showNote={showNote} note={note} setNote={setNote} />

            {/* Input row: [input + clear X] */}
            <div style={{ position: "relative" }}>
                <input
                    ref={inputRef}
                    type="text"
                    className="form-control highlight"
                    value={data}
                    name={"dmData" + index}
                    onChange={(e) => onDataChange(index, e.target.value)}
                    onClick={handleInputClick}
                    placeholder="D.Matrix content..."
                    style={{
                        borderWidth: "2px",
                        paddingTop: "2px",
                        paddingBottom: "2px",
                        paddingRight: data ? "26px" : undefined,
                    }}
                />
                {data && (
                    <button
                        type="button"
                        onClick={handleClearClick}
                        title="Clear input"
                        style={{
                            position: "absolute",
                            right: "8px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            background: "none",
                            border: "none",
                            padding: "2px",
                            cursor: "pointer",
                            color: "#bbb",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            lineHeight: 1,
                            transition: "color 0.2s ease",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = "#666")}
                        onMouseLeave={(e) => (e.currentTarget.style.color = "#bbb")}
                    >
                        <CloseCircleFilled style={{ fontSize: "15px" }} />
                    </button>
                )}
            </div>

            {/* DISPLAY */}
            <div
                className="qr-display w-100"
                style={{
                    paddingBottom: "100%",
                    border: "2px solid #c5c9cc",
                    borderRadius: "12px",
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "10px",
                    }}
                >
                    {/* Placeholder when empty */}
                    {isEmpty && !hasError && (
                        <NoDataPlaceholder
                            onClick={() => { if (inputRef.current) inputRef.current.focus(); }}
                            onMouseEnter={() => { if (inputRef.current) inputRef.current.placeholder = "Enter data here!"; }}
                        />
                    )}

                    {/* Error state */}
                    {hasError && (
                        <ErrorPlaceholder
                            message="Cannot encode this data"
                            onClick={() => { if (inputRef.current) inputRef.current.focus(); }}
                            onMouseEnter={() => { if (inputRef.current) inputRef.current.placeholder = "Enter data here!"; }}
                        />
                    )}

                    {/* Canvas */}
                    <canvas
                        ref={canvasRef}
                        style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            display: isEmpty || hasError ? "none" : "block",
                            imageRendering: "pixelated",
                        }}
                    />
                </div>
            </div>

            {/* Actions */}
            <div className="w-100" style={{ display: "flex", gap: "15px", justifyContent: "space-between" }}>
                <button
                    type="button"
                    className="btn btn-outline-danger btn-sm rounded-pill d-flex align-items-center justify-content-center p-0"
                    style={{ width: "36px", height: "36px", flexShrink: 0 }}
                    onClick={onRemove}
                    title="Remove"
                    disabled={!showRemove}
                >
                    <DeleteOutlined style={{ fontSize: "16px" }} />
                </button>
                <TheNoteToggleButton showNote={showNote} setShowNote={setShowNote} />
                <button
                    type="button"
                    className="btn btn-success btn-sm rounded-pill d-flex align-items-center justify-content-center p-0"
                    style={{ width: "36px", height: "36px", flexShrink: 0 }}
                    onClick={handleDownloadPNG}
                    title="Download PNG"
                    disabled={!canDownload}
                >
                    <DownloadOutlined style={{ fontSize: "16px" }} />
                </button>
            </div>
        </div>
    );
}

export default RenderOneDataMatrix;
